import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  CashSpecialIcon,
  Layout,
  PageTitle,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useCashDepositContext } from "../hooks/useCashDepositContext";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { TITLE_COLOR } from "../constants";
import {
  appendAnalyticsHandoff,
  getAnalyticsSession,
  getEvents,
  safeCapture,
} from "../analytics";
import { cashDepositNextRoute, isFundingPolicyReady } from "../utils/funding-policy";
import { RequestStatus } from "../utils/commons";
import { useLiveExitUrl } from "../hooks/useLiveExitUrl";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";

/**
 * Entry screen of the cash-deposit flow. Greets the user and invites them to
 * make a cash deposit at thousands of physical locations. Next step is the
 * chain picker only when Superadmin requires the customer to choose a chain; otherwise
 * the barcode screen.
 *
 * When the link carries `config.callbackUrl` (mint from a host widget, or a
 * standalone partner return URL), the header chevron leaves this origin via
 * that URL — never `history.back()`, which would re-hit the host launcher.
 */
export const CashDepositLandingPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors, backgroundIcon } =
    colorConfig;
  const { redirectTo } = useRouteWithToken();
  const { context, status } = useCashDepositContext();
  const callbackUrl = useLiveExitUrl();

  useEffect(() => {
    safeCapture(getEvents().LANDING_VIEW);
  }, []);

  // The greeting shows the sender — the person funding / making the cash
  // deposit. It is resolved from the dedicated context endpoint (the operation
  // for operation-funded links, the sender config for standalone links), with
  // a fallback to the claimed session's userData.
  const firstName =
    context?.sender?.firstName ??
    session.data?.userData?.name?.firstName ??
    session.data?.userData?.fullName?.split(" ")[0] ??
    "";

  const handleContinue = () => {
    if (!isFundingPolicyReady(context) && status !== RequestStatus.ERROR) {
      return;
    }
    redirectTo(cashDepositNextRoute(context));
  };

  const handleBackToHost = () => {
    if (!callbackUrl) return;
    safeCapture(getEvents().LANDING_BACK_CLICK);
    const { sessionId, distinctId } = getAnalyticsSession();
    const handoffUrl =
      sessionId && distinctId
        ? appendAnalyticsHandoff(callbackUrl, { sessionId, distinctId })
        : callbackUrl;
    window.location.href = handoffUrl;
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="need-help-header flow-landing-page">
        <Layout.Header>
          <Navbar
            title={t("transactions.needHelp")}
            align="right"
            color={bodyCopy}
            {...(callbackUrl
              ? { onBackClick: handleBackToHost }
              : { noBackButton: true })}
            onIconClick={() => openSupport()}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="landing-section flow-hero-section flow-hero-section--with-header">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark
              dropzoneColors={dropzoneColors}
              backgroundIcon={backgroundIcon}
            >
              <CashSpecialIcon size={60} colors={dropzoneColors} />
            </FlowHeroMark>
            <section className="landing-container landing-container--tight">
              <PageTitle
                highlight={
                  firstName
                    ? `${t("cashDeposit.greeting")} ${firstName}`
                    : t("cashDeposit.greeting")
                }
                highlightColor={TITLE_COLOR}
              />
              <BodyCopy textColor={bodyCopy} className="cash-deposit-landing-body">
                {t("cashDeposit.body")}
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={
                !isFundingPolicyReady(context) &&
                status !== RequestStatus.ERROR
              }
              onClick={handleContinue}
            >
              {t("cashDeposit.cta")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
